@if (currentMetadata(); as meta) {
  <div class="h-full overflow-auto">
    <!-- Status and metadata section -->
    <div class="mb-3 p-3 bg-ds-bg rounded-ds-md border border-ds-border">
      <div class="flex justify-between items-center mb-2">
        <h3 class="text-sm font-semibold font-data text-ds-text" data-testid="ocr-result-doc-info-title">Document Information</h3>
        <div class="flex items-center gap-1">
          <span [class]="'text-sm font-semibold font-data mr-1 ' + statusClass()">{{ statusText() }}</span>
          <app-ocr-anchors [documentAnchors]="documentAnchors()" [templateMatchResult]="templateMatchResult()"></app-ocr-anchors>
          <app-ocr-facts [facts]="processingMetadata()" [cost]="runCost()"></app-ocr-facts>
          <app-webhook-delivery [s3Key]="meta.s3Key" [delivery]="meta.webhookDelivery"></app-webhook-delivery>
        </div>
      </div>

      <div class="grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-1.5 text-sm">
        <div class="flex items-center flex-wrap gap-1">
          <span class="text-xs font-medium text-ds-text-muted">Document:</span>
          @if (!isInOcrView) {
            <a
              class="ml-1 font-data text-ds-primary hover:underline truncate max-w-[200px] cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ds-secondary rounded-ds-sm"
              [routerLink]="['/ocr-view']"
              [queryParams]="{ s3Key: meta.s3Key }"
              target="_blank"
              rel="noopener noreferrer"
              [matTooltip]="meta.originalFileName || 'Open PDF & details in a new tab'"
              aria-label="Open PDF & details in a new tab"
            >
              {{ meta.originalFileName }}
            </a>
            <a
              class="ml-2 inline-flex items-center justify-center text-ds-primary hover:text-ds-primary-hover cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ds-secondary rounded-ds-sm"
              [routerLink]="['/ocr-view']"
              [queryParams]="{ s3Key: meta.s3Key }"
              target="_blank"
              rel="noopener noreferrer"
              matTooltip="Open PDF & details in a new tab"
              aria-label="Open PDF & details in a new tab"
            >
              <mat-icon aria-hidden="true" class="!text-base align-middle">open_in_new</mat-icon>
            </a>
          } @else {
            <span class="ml-1 font-data text-ds-text truncate max-w-[300px]" [matTooltip]="meta.originalFileName || ''">{{ meta.originalFileName }}</span>
          }
        </div>
        <div>
          <span class="text-xs font-medium text-ds-text-muted">Processed:</span>
          <span class="ml-1 font-data text-ds-text">{{ formattedTimestamp() }}</span>
        </div>
        @if (meta.usedTemplateId) {
          <div class="flex items-center flex-wrap gap-1">
            <span class="text-xs font-medium text-ds-text-muted">Template Name:</span>
            <a [routerLink]="['/templates', meta.usedTemplateId]" target="_blank"
               class="ml-1 font-data text-ds-primary hover:underline cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ds-secondary rounded-ds-sm">
              {{ templateName() || meta.usedTemplateId }}
            </a>
            @if (isOutOfSync()) {
              <span class="inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full border border-ds-warning text-ds-warning bg-ds-warning/10"
                    matTooltip="The template structure changed after this document was processed">
                <mat-icon aria-hidden="true" class="!text-[14px] !w-3.5 !h-3.5 !leading-none">sync_problem</mat-icon>
                Out of sync
              </span>
            }
          </div>
        }
        @if (structureName(); as sName) {
          <div>
            <span class="text-xs font-medium text-ds-text-muted">Structure:</span>
            <a routerLink="/fields" [queryParams]="{ structureId: structureId() }"
               class="ml-1 font-data text-ds-primary hover:underline inline-flex items-center gap-1 cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ds-secondary rounded-ds-sm">
              {{ sName }}
              <mat-icon class="!text-sm !w-4 !h-4">open_in_new</mat-icon>
            </a>
          </div>
        }
      </div>
    </div>

    <!-- Error message section -->
    @if (meta.status === 'error') {
      <div class="mb-3 p-3 bg-red-50 dark:bg-red-900/20 rounded-ds-md border border-red-200 dark:border-red-800">
        <div class="flex items-start flex-col justify-between gap-2">
          <div class="flex w-full justify-end">
            <button data-testid="ocr-result-reprocess-template-error-btn"
                    class="shrink-0 inline-flex items-center gap-1 px-2.5 h-8 text-xs font-medium rounded-ds-sm border border-ds-primary text-ds-primary bg-ds-surface hover:bg-ds-primary/5 cursor-pointer transition-colors duration-150 disabled:opacity-50 disabled:cursor-not-allowed focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ds-secondary"
                    (click)="openReprocessDialog()" [disabled]="saving() || hasAnyEditing()"
                    title="Pick a template and re-run OCR on this document">
              <mat-icon aria-hidden="true" class="!text-[16px] !w-4 !h-4 !leading-4">restart_alt</mat-icon>
              Reprocess document
            </button>
          </div>
          <div class="min-w-0 flex-1">
            <h3 class="text-sm font-semibold font-data text-red-700 dark:text-red-400 mb-1">Error</h3>
            @if (meta.errorMessage) {
              <app-ocr-error [errorMessage]="meta.errorMessage" [trace]="anchorTreeTrace()"></app-ocr-error>
            } @else if (anchorTreeTrace()) {
              <app-ocr-error [trace]="anchorTreeTrace()"></app-ocr-error>
            }
          </div>
        </div>
      </div>
    }

    <!-- Processing section -->
    @if (meta.status === 'processing') {
      <div class="mb-3 p-3 bg-blue-50 dark:bg-blue-900/20 rounded-ds-md border border-blue-200 dark:border-blue-800 flex items-center">
        <mat-icon class="animate-spin -ml-1 mr-3 text-blue-600 dark:text-blue-400" aria-hidden="true">autorenew</mat-icon>
        <span class="text-sm text-blue-700 dark:text-blue-400">Processing document. This may take a few moments...</span>
      </div>
    }

    <!-- OCR Results (with Judgment) -->
    @if ((ocrResultObject() || meta.ocrJudgeResult)) {
      <div class="mb-3">
        <h3 class="text-sm font-semibold font-data mb-2 text-ds-text">OCR Results</h3>
        <div class="bg-ds-surface rounded-ds-md border border-ds-border p-3">
          @if (meta.ocrJudgeResult) {
            <div class="flex flex-wrap items-center gap-2 mb-3">
              <span class="px-2 py-0.5 inline-flex text-xs leading-5 font-semibold rounded-full" [class]="verdictBadgeClass()">
                {{ meta.ocrJudgeResult.overallVerdict }}
              </span>
              <span class="text-xs font-data text-ds-text-muted">Confidence: {{ toPercent(meta.ocrJudgeResult.overallConfidence) }}</span>
              @if (meta.ocrJudgeResult.comments) {
                <span class="text-xs text-ds-text-muted">— {{ meta.ocrJudgeResult.comments }}</span>
              }
            </div>
          }
          @if (meta.status === 'done') {
            @if (!meta.reviewed && meta.ocrJudgeResult && (meta.ocrJudgeResult.overallVerdict === 'fail' || meta.ocrJudgeResult.overallVerdict === 'needs_review')) {
              <div class="mb-3 flex justify-end gap-2">
                <button data-testid="ocr-result-reprocess-template-btn"
                        class="inline-flex items-center gap-1 px-2.5 h-8 text-xs font-medium rounded-ds-sm border border-ds-primary text-ds-primary bg-ds-surface hover:bg-ds-primary/5 cursor-pointer transition-colors duration-150 disabled:opacity-50 disabled:cursor-not-allowed focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ds-secondary"
                        (click)="openReprocessDialog()" [disabled]="saving() || hasAnyEditing()" title="Pick a template and re-run OCR on this document">
                  <mat-icon aria-hidden="true" class="!text-[16px] !w-4 !h-4 !leading-4">restart_alt</mat-icon>
                  Reprocess document
                </button>
                <button data-testid="ocr-result-mark-reviewed-btn"
                        class="inline-flex items-center gap-1 px-2.5 h-8 text-xs font-semibold rounded-ds-sm bg-ds-primary text-ds-on-primary hover:bg-ds-primary-hover cursor-pointer transition-colors duration-150 disabled:opacity-50 disabled:cursor-not-allowed focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ds-secondary focus-visible:ring-offset-1"
                        (click)="markReviewed()" [disabled]="saving() || hasAnyEditing()" title="Mark this document as reviewed/finalized">
                  <mat-icon aria-hidden="true" class="!text-[16px] !w-4 !h-4 !leading-4">task_alt</mat-icon>
                  Mark as Reviewed
                </button>
              </div>
            } @else if (meta.reviewed) {
              <div class="mb-3 flex justify-end">
                <div class="text-right">
                  <span class="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300">
                    <mat-icon aria-hidden="true" class="!text-[14px] !w-3.5 !h-3.5 !leading-none text-green-700 dark:text-green-300">thumb_up</mat-icon>
                    Finalized
                  </span>
                  @if (meta.reviewedBy) {
                    <div class="text-xs font-data text-ds-text-muted mt-1">Reviewed By: {{ meta.reviewedBy }}</div>
                  }
                  @if (meta.reviewedAt) {
                    <div class="text-xs font-data text-ds-text-muted">Reviewed At: {{ meta.reviewedAt | date:'medium' }}</div>
                  }
                </div>
              </div>
            }
          }
          <div class="grid grid-cols-1 gap-1.5">
            @for (field of combinedFieldKeys(); track field) {
              @if (fieldsMap(); as fm) {
              <div class="field-card p-2 rounded-ds-sm border border-ds-border bg-ds-bg group relative hover:border-ds-secondary transition-colors duration-150"
                   [class.!border-ds-primary]="isEditing(field)"
                   [class.!bg-ds-surface]="isEditing(field)">
                  @if (isEditing(field)) {
                    <div class="flex flex-col gap-2">
                      <div class="flex items-start justify-between">
                        <div class="min-w-0">
                          <div class="text-sm font-medium text-ds-text break-words">{{ field }}</div>
                          @if (!isObjectType(fm[field]?.type)) {
                            <div class="text-sm ml-2 font-data text-ds-text break-words">
                              <app-default-value-input
                                [fieldType]="fm[field].type || 'string'"
                                [ngModel]="getEditedValue(field)"
                                (ngModelChange)="setEditedValue(field, $event)"
                              ></app-default-value-input>
                            </div>
                          }
                          @if (fm[field].description) {
                            <div class="mt-1 text-xs text-ds-text-muted break-words">{{ fm[field].description }}</div>
                          }
                          @if (template()?.structure?.[fm[field].id]?.description; as tplDesc) {
                            <div class="mt-1 text-xs text-ds-text break-words"><span class="font-medium">Template instructions:</span> {{ tplDesc }}</div>
                          }
                        </div>
                        <div class="ml-4 text-right">
                          @if (getAssessment(field); as fa) {
                            <span class="px-1.5 py-0.5 rounded text-xs font-semibold inline-block mb-1"
                                  [class.bg-green-100]="fa?.correct"
                                  [class.text-green-800]="fa?.correct"
                                  [class.dark:bg-green-900]="fa?.correct"
                                  [class.dark:text-green-400]="fa?.correct"
                                  [class.bg-red-100]="fa && !fa.correct"
                                  [class.text-red-800]="fa && !fa.correct"
                                  [class.dark:bg-red-900]="fa && !fa.correct"
                                  [class.dark:text-red-400]="fa && !fa.correct">
                              {{ fa?.correct ? 'Correct' : 'Needs Fix' }}
                            </span>
                            @if (fa?.manualOverride) {
                              <span class="px-1.5 py-0.5 rounded text-xs font-semibold inline-block mb-1 bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-300">Overridden</span>
                            }
                            <div class="text-xs font-data text-ds-text-muted">Confidence: {{ toPercent(fa.confidence) }}</div>
                          }
                        </div>
                      </div>
                      @if (isObjectType(fm[field]?.type)) {
                        <div class="w-full">
                          <json-editor
                            [options]="jsonEditorOptions"
                            [data]="getEditedValue(field)"
                            (jsonChange)="onJsonEditorChange(field, $event)"
                            style="display: block; width: 100%;"
                          ></json-editor>
                        </div>
                      }
                      <div class="mt-2">
                        <textarea
                          class="w-full text-sm p-2 rounded-ds-sm border border-ds-border bg-ds-surface text-ds-text placeholder:text-ds-text-muted focus:outline-none focus:ring-2 focus:ring-ds-secondary focus:border-ds-secondary transition-colors duration-150"
                          placeholder="Why are you correcting this? (optional)"
                          rows="2"
                          [ngModel]="getCorrectionComment(field)"
                          (ngModelChange)="setCorrectionComment(field, $event)"
                          data-testid="ocr-result-correction-comment"
                        ></textarea>
                      </div>
                      @if (saveError()) {
                        <div class="text-xs text-red-600 dark:text-red-400">{{ saveError() }}</div>
                      }
                      @if (getAssessment(field)?.comment; as comment) {
                        <div class="text-right text-xs text-ds-text-muted mt-1 max-w-s">{{ comment }}</div>
                      }
                      <div class="flex gap-2 justify-end items-center">
                        @if (meta.usedTemplateId) {
                          <a class="mr-auto text-xs text-ds-primary hover:underline flex items-center content-center gap-1 cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ds-secondary rounded-ds-sm"
                          [routerLink]="['/edit-template', meta.usedTemplateId]"
                          [queryParams]="{ highlightField: fm[field].id }">
                            <mat-icon class="!text-s">settings</mat-icon>
                            Edit Template
                          </a>
                          <a class="mr-auto text-xs text-ds-warning hover:underline flex items-center content-center gap-1 cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ds-secondary rounded-ds-sm"
                             [routerLink]="['/fields']"
                             [queryParams]="{ fieldId: fm[field].id }"
                             title="Changing this field affects every template that uses it">
                            <mat-icon class="!text-s">warning_amber</mat-icon>
                            Edit Field (affects other templates)
                          </a>
                        }
                        <button data-testid="ocr-result-field-cancel-btn"
                                class="px-2.5 h-8 text-xs font-medium rounded-ds-sm border border-ds-border bg-ds-surface text-ds-text hover:bg-ds-surface-muted cursor-pointer transition-colors duration-150 disabled:opacity-50 disabled:cursor-not-allowed focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ds-secondary"
                                (click)="cancelEdit(field)" [disabled]="saving()">Cancel</button>
                        <button data-testid="ocr-result-field-save-btn"
                                class="px-2.5 h-8 text-xs font-semibold rounded-ds-sm bg-ds-primary text-ds-on-primary hover:bg-ds-primary-hover cursor-pointer transition-colors duration-150 disabled:opacity-50 disabled:cursor-not-allowed focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ds-secondary focus-visible:ring-offset-1"
                                (click)="saveEdit(field, fm[field].type)" [disabled]="saving() || hasJsonError(field)">Save</button>
                      </div>
                    </div>
                  } @else {
                    <div class="flex items-start justify-between">
                      <div class="min-w-0 flex-1">
                        <div class="flex items-center gap-1">
                          <span class="text-sm font-medium text-ds-text break-words">{{ field }}</span>
                          @if (isObjectType(fm[field]?.type)) {
                            <span class="text-[10px] font-semibold font-data px-1.5 py-0.5 rounded-full bg-ds-secondary/10 text-ds-secondary border border-ds-secondary/30">
                              {{ fm[field].type === 'object-list' ? 'list[' + getListItems(ocrResultObject()?.[field]).length + ']' : 'object' }}
                            </span>
                          }
                        </div>
                        <!-- Nested object display -->
                        @if (fm[field]?.type === 'object' && getNestedKeys(ocrResultObject()?.[field]).length > 0) {
                          <div class="mt-1 ml-2">
                            <div class="flex items-center gap-2">
                              <button class="flex items-center gap-1 text-xs text-ds-primary hover:underline cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ds-secondary rounded-ds-sm"
                                      (click)="toggleFieldExpanded(field)">
                                <mat-icon class="!text-sm !w-4 !h-4 transition-transform duration-200" [class.rotate-180]="isFieldExpanded(field)">expand_more</mat-icon>
                                {{ isFieldExpanded(field) ? 'Collapse' : 'Expand' }} ({{ getNestedKeys(ocrResultObject()[field]).length }} fields)
                              </button>
                              @if (!isReadOnly()) {
                                <button class="inline-flex items-center justify-center p-1 rounded-ds-sm cursor-pointer hover:bg-ds-surface-muted text-ds-text-muted hover:text-ds-primary opacity-0 group-hover:opacity-100 focus:opacity-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-ds-secondary transition-colors duration-150"
                                        aria-label="Edit field"
                                        title="Edit JSON"
                                        (click)="startEdit(field, fm[field].type)">
                                  <mat-icon aria-hidden="true" class="!text-sm">edit</mat-icon>
                                </button>
                              }
                            </div>
                            @if (isFieldExpanded(field)) {
                              <div class="mt-2 space-y-1 rounded-ds-md border border-dashed border-ds-border bg-ds-surface p-3">
                                @for (childField of getChildFields(fm[field]); track childField.id) {
                                  <div class="flex items-baseline gap-2 py-1 border-b border-ds-border/60 last:border-0">
                                    <span class="text-xs font-medium text-ds-text-muted min-w-[100px] shrink-0">{{ childField.name }}</span>
                                    <span class="text-sm font-data text-ds-text break-words">{{ formatScalarValue(ocrResultObject()[field][childField.name]) }}</span>
                                  </div>
                                }
                                @for (key of getNestedKeys(ocrResultObject()[field]); track key) {
                                  @if (!getChildFields(fm[field]).length) {
                                    <div class="flex items-baseline gap-2 py-1 border-b border-ds-border/60 last:border-0">
                                      <span class="text-xs font-medium text-ds-text-muted min-w-[100px] shrink-0">{{ key }}</span>
                                      <span class="text-sm font-data text-ds-text break-words">{{ formatScalarValue(ocrResultObject()[field][key]) }}</span>
                                    </div>
                                  }
                                }
                              </div>
                            }
                          </div>
                        }
                        @else if (fm[field]?.type === 'object-list' && getListItems(ocrResultObject()?.[field]).length >= 0) {
                        <!-- Object-list display -->
                          <div class="mt-1 ml-2">
                            <div class="flex items-center gap-2">
                              <button class="flex items-center gap-1 text-xs text-ds-primary hover:underline cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ds-secondary rounded-ds-sm"
                                      (click)="toggleFieldExpanded(field)">
                                <mat-icon class="!text-sm !w-4 !h-4 transition-transform duration-200" [class.rotate-180]="isFieldExpanded(field)">expand_more</mat-icon>
                                {{ isFieldExpanded(field) ? 'Collapse' : 'Expand' }} {{ getListItems(ocrResultObject()[field]).length }} item(s)
                              </button>
                              @if (!isReadOnly()) {
                                <button class="inline-flex items-center justify-center p-1 rounded-ds-sm cursor-pointer hover:bg-ds-surface-muted text-ds-text-muted hover:text-ds-primary opacity-0 group-hover:opacity-100 focus:opacity-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-ds-secondary transition-colors duration-150"
                                        aria-label="Edit field"
                                        title="Edit JSON"
                                        (click)="startEdit(field, fm[field].type)">
                                  <mat-icon aria-hidden="true" class="!text-sm">edit</mat-icon>
                                </button>
                              }
                            </div>
                            @if (isFieldExpanded(field)) {
                              <div class="mt-2 space-y-3">
                                @for (item of getListItems(ocrResultObject()[field]); track $index) {
                                  <div class="rounded-ds-md border border-dashed border-ds-border bg-ds-surface p-3">
                                    <span class="block text-[10px] font-semibold font-data text-ds-text-muted mb-2">Item {{ $index + 1 }}</span>
                                    @if (getChildFields(fm[field]).length) {
                                      @for (childField of getChildFields(fm[field]); track childField.id) {
                                        <div class="flex items-baseline gap-2 py-1 border-b border-ds-border/60 last:border-0">
                                          <span class="text-xs font-medium text-ds-text-muted min-w-[100px] shrink-0">{{ childField.name }}</span>
                                          <span class="text-sm font-data text-ds-text break-words">{{ formatScalarValue(item[childField.name]) }}</span>
                                        </div>
                                      }
                                    } @else {
                                      @for (key of getNestedKeys(item); track key) {
                                        <div class="flex items-baseline gap-2 py-1 border-b border-ds-border/60 last:border-0">
                                          <span class="text-xs font-medium text-ds-text-muted min-w-[100px] shrink-0">{{ key }}</span>
                                          <span class="text-sm font-data text-ds-text break-words">{{ formatScalarValue(item[key]) }}</span>
                                        </div>
                                      }
                                    }
                                  </div>
                                }
                              </div>
                            }
                          </div>
                        }
                        @else {
                        <!-- Scalar value display (original behavior) -->
                          <div class="text-sm ml-2 font-data text-ds-text break-words flex items-center gap-2">
                            @if (!isReadOnly()) {
                              <button class="inline-flex items-center justify-center p-1 rounded-ds-sm cursor-pointer hover:bg-ds-surface-muted text-ds-text-muted hover:text-ds-primary opacity-0 group-hover:opacity-100 focus:opacity-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-ds-secondary disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-150"
                                      aria-label="Edit field"
                                      title="Edit"
                                      [disabled]="isReadOnly()"
                                      (click)="startEdit(field, fm[field].type)">
                                <mat-icon aria-hidden="true" class="!text-base">edit</mat-icon>
                              </button>
                            }
                            <span class="break-words">{{ formatValue(ocrResultObject() ? ocrResultObject()[field] : '') }}</span>
                            @if (ocrResultObject() && ocrResultObject()[field]) {
                              <button class="inline-flex items-center justify-center p-1 rounded-ds-sm cursor-pointer text-ds-text-muted hover:text-ds-primary hover:bg-ds-surface-muted opacity-0 group-hover:opacity-100 focus:opacity-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-ds-secondary transition-colors duration-150"
                                      aria-label="Copy field value"
                                      title="Copy"
                                      (click)="copyToClipboard(ocrResultObject()[field])">
                                <mat-icon aria-hidden="true" class="!text-sm">content_copy</mat-icon>
                              </button>
                            }
                          </div>
                        }
                      </div>
                      <div class="ml-4 text-right flex items-start gap-2">
                        @if (!isReadOnly() && isObjectType(fm[field]?.type)) {
                          <button class="inline-flex items-center justify-center p-1 rounded-ds-sm cursor-pointer text-ds-text-muted hover:text-ds-primary hover:bg-ds-surface-muted opacity-0 group-hover:opacity-100 focus:opacity-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-ds-secondary transition-colors duration-150"
                                  aria-label="Copy field value"
                                  title="Copy JSON"
                                  (click)="copyToClipboard(ocrResultObject()?.[field])">
                            <mat-icon aria-hidden="true" class="!text-sm">content_copy</mat-icon>
                          </button>
                        }
                        @if (getAssessment(field); as fa) {
                          <div>
                            <span class="px-1.5 py-0.5 rounded text-xs font-semibold inline-block mb-1"
                                  [class.bg-green-100]="fa?.correct"
                                  [class.text-green-800]="fa?.correct"
                                  [class.dark:bg-green-900]="fa?.correct"
                                  [class.dark:text-green-400]="fa?.correct"
                                  [class.bg-red-100]="fa && !fa.correct"
                                  [class.text-red-800]="fa && !fa.correct"
                                  [class.dark:bg-red-900]="fa && !fa.correct"
                                  [class.dark:text-red-400]="fa && !fa.correct">
                              {{ fa?.correct ? 'Correct' : 'Needs Fix' }}
                            </span>
                            @if (fa?.manualOverride) {
                              <span class="px-1.5 py-0.5 rounded text-xs font-semibold inline-block mb-1 bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-300">Overridden</span>
                            }
                            <div class="text-xs font-data text-ds-text-muted">Confidence: {{ toPercent(fa.confidence) }}</div>
                          </div>
                        }
                      </div>
                    </div>
                    @if (getAssessment(field)?.comment; as comment) {
                      <div class="text-right text-xs text-ds-text-muted mt-1 max-w-s">{{ comment }}</div>
                    }
                    @if (correctionsByField()[field]; as fieldCorrections) {
                      @if (fieldCorrections.length > 0) {
                        <div class="mt-2 border-t border-ds-border pt-2">
                          <div class="flex items-center gap-1 text-xs text-ds-warning font-medium mb-1">
                            <mat-icon class="!text-sm !w-4 !h-4">history</mat-icon>
                            Correction History ({{ fieldCorrections.length }})
                          </div>
                          @for (correction of fieldCorrections; track correction.id) {
                            <div class="text-xs font-data text-ds-text-muted ml-5 mb-1">
                              <span class="text-ds-danger line-through">{{ formatValue(correction.fromValue) }}</span>
                              <mat-icon class="!text-xs !w-3 !h-3 align-middle mx-0.5">arrow_forward</mat-icon>
                              <span class="text-ds-success">{{ formatValue(correction.toValue) }}</span>
                              <span class="ml-1">by {{ correction.correctedBy }} · {{ correction.correctedAt | date:'short' }}</span>
                              @if (correction.comment) {
                                <div class="italic ml-1 font-body">"{{ correction.comment }}"</div>
                              }
                            </div>
                          }
                        </div>
                      }
                    }
                }
              </div>
              }
            }
          </div>
        </div>
      </div>
    }


    <!-- No results yet section -->
    @if (!ocrResultObject() && !meta.ocrJudgeResult && meta.status !== 'error' && meta.status !== 'processing') {
      <div class="flex items-center justify-center h-32 text-sm text-ds-text-muted bg-ds-bg rounded-ds-md border border-ds-border">
        <p>No OCR results available yet.</p>
      </div>
    }
  </div>
} @else {
  <!-- No metadata state -->
  <div class="flex items-center justify-center h-full text-sm text-ds-text-muted">
    <p>No document has been processed.</p>
  </div>
}
